/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useLoanContext } from '../../contexts/LoanContext';
import { useCallback, useState } from 'react';
import { isObjEmptyOrNull, isStrEmptyOrNull, getErrorFromEndpoint } from '../UtilityFunctions';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';
import { AxiosError } from 'axios';

const useEventTrigger = () => {
  const { token } = useAuthContext();
  const { get } = useAbortController();
  const { loanNumber } = useLoanContext();
  const [eventTriggerResp, setEventTriggerResp] = useState(null);
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);

  const handleError = useCallback((res: Error | AxiosError | any) => {
    const err = getErrorFromEndpoint(res);
    if (isStrEmptyOrNull(err)) return '';
    console.log('Error in eventTriggerAPI in useEventTrigger hook: ' + err);
    setErrors(err);
    return err;
  }, []);

  const EventTriggerAPI = useCallback(
    async (keyword: string): Promise<UnknownEventResp> => {
      if (!token || !loanNumber || isStrEmptyOrNull(keyword)) return null;
      var urlPath = process.env.REACT_APP_EVENT_TRIGGER.replace('?', loanNumber) + keyword;
      let req = { url: urlPath, method: 'POST', data: {} };
      let hdrs = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const conf = get();
      //console.log('req', request);
      const res = await axiosCall(req, conf, hdrs)
        .then((res) => {
          const err = handleError(res);
          if (isObjEmptyOrNull(res) || !isStrEmptyOrNull(err)) {
            return null;
          }
          return res;
        })
        .catch((e: AxiosError | Error | any) => {
          handleError(e);
          return null;
        });
      setEventTriggerResp(res);
      return res;
    },
    [get, token, handleError, loanNumber],
  );

  const triggerEvent = useCallback(
    async (keyword: string): Promise<UnknownEventResp> => {
      setLoading(true);
      setEventTriggerResp(null);
      const resp = await EventTriggerAPI(keyword).finally(() => {
        setLoading(false);
      });
      return resp;
    },
    [EventTriggerAPI],
  );

  return { eventTriggerResp, loading, errors, triggerEvent };
};

export default useEventTrigger;
